import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Database, Download, FileJson, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { downloadFile, formatApiError } from "../lib/api";

const when = (iso) =>
  iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";
const size = (bytes) =>
  bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const KIND_LABEL = { manual: "On demand", nightly: "Nightly", safety: "Pre-restore safety" };

/** Database backups, the plain-English config export, and restore. */
export default function AdminBackup() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [restore, setRestore] = useState({ filename: "", confirm: "" });
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    const { data: payload } = await api.get("/admin/backups");
    setData(payload);
  }, []);

  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [load]);

  const takeBackup = async () => {
    setBusy("create");
    try {
      const { data: entry } = await api.post("/admin/backups");
      toast.success(`Backup taken (${size(entry.size_bytes)})`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const runRestore = async () => {
    const label = file ? file.name : restore.filename;
    if (!window.confirm(`Replace every record in this environment's database with "${label}"? A safety backup is taken first.`)) return;
    setBusy("restore");
    try {
      const body = new FormData();
      body.append("confirm", restore.confirm);
      if (file) body.append("file", file);
      else body.append("filename", restore.filename);
      const { data: payload } = await api.post("/admin/backups/restore", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(payload);
      setRestore({ filename: "", confirm: "" });
      setFile(null);
      toast.success("Restore complete");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!data) return <div className="p-10 eyebrow">Loading…</div>;

  const canRestore = restore.confirm.trim().toUpperCase() === "RESTORE" && (file || restore.filename);

  return (
    <div className="mx-auto max-w-[1400px] px-6 py-10 md:py-12 lg:px-10" data-testid="admin-backup-page">
      <div className="eyebrow mb-2">System · Backup and restore</div>
      <h1 className="font-heading font-black text-4xl md:text-5xl leading-none tracking-tight mb-3" style={{ color: "var(--ink)" }}>
        Backup
      </h1>
      <p className="text-sm max-w-3xl mb-10" style={{ color: "var(--muted)" }}>
        A backup is a complete snapshot of this environment's database taken at the moment it runs.
        Nightly backups are kept for {data.retention_days} days; backups you take yourself are kept
        until you remove them. The configuration export is a readable file of the setup — employers,
        rates, carriers, vendors, users and release switches — with no invoices, payments or
        credentials in it.
      </p>

      <div className="grid gap-6 lg:grid-cols-2 mb-10">
        <div className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <div className="eyebrow flex items-center gap-2"><Database size={13}/> Database backup</div>
          <p className="mt-2 mb-5 text-sm" style={{ color: "var(--muted)" }}>
            A gzip mongodump archive of every collection, restorable from this page.
          </p>
          <button onClick={takeBackup} className="btn-primary flex items-center gap-2"
                  disabled={busy === "create"} data-testid="take-backup">
            <Database size={14}/> {busy === "create" ? "Taking backup…" : "Take backup now"}
          </button>
        </div>
        <div className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
          <div className="eyebrow flex items-center gap-2"><FileJson size={13}/> Configuration export</div>
          <p className="mt-2 mb-5 text-sm" style={{ color: "var(--muted)" }}>
            Readable JSON of the setup, for reviewing or re-keying — not a database restore.
          </p>
          <button
            onClick={() => downloadFile("/admin/backups/config-export", "portal-config.json")}
            className="btn-ghost flex items-center gap-2" data-testid="download-config-export"
          >
            <Download size={14}/> Download config export
          </button>
        </div>
      </div>

      <h2 className="font-heading font-bold text-2xl mb-4" style={{ color: "var(--ink)" }}>Stored backups</h2>
      <div className="border max-h-[460px] overflow-auto mb-12" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="backups-table-region">
        <table className="w-full text-sm min-w-[720px]" data-testid="backups-table">
          <thead>
            <tr className="border-b eyebrow text-left" style={{ borderColor: "var(--border)" }}>
              <th className="px-4 py-3">Taken</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Size</th>
              <th className="px-4 py-3">By</th>
              <th className="px-4 py-3">Off-pod copy</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.backups.map((entry) => (
              <tr key={entry.filename} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`backup-row-${entry.filename}`}>
                <td className="px-4 py-3" style={{ color: "var(--ink)" }}>{when(entry.created_at)}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{KIND_LABEL[entry.kind] || entry.kind}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{size(entry.size_bytes)}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{entry.created_by || "—"}</td>
                <td className="px-4 py-3" style={{ color: "var(--muted)" }}>{entry.mirrored ? "Yes" : "Local only"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    className="btn-ghost text-xs mr-2"
                    onClick={() => downloadFile(`/admin/backups/${entry.filename}/download`, entry.filename)}
                    data-testid={`download-backup-${entry.filename}`}
                  >
                    Download
                  </button>
                  <button
                    className="btn-ghost text-xs"
                    onClick={() => { setFile(null); setRestore({ filename: entry.filename, confirm: "" }); }}
                    data-testid={`select-restore-${entry.filename}`}
                  >
                    Restore…
                  </button>
                </td>
              </tr>
            ))}
            {!data.backups.length && (
              <tr><td colSpan={6} className="p-10 text-center text-sm" style={{ color: "var(--muted)" }} data-testid="backups-empty">
                No backups yet. Take one now, or wait for tonight's.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 className="font-heading font-bold text-2xl mb-4 flex items-center gap-2" style={{ color: "var(--ink)" }}>
        <RotateCcw size={18} style={{ color: "var(--muted)" }}/> Restore
      </h2>
      <div className="border p-6" style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }} data-testid="restore-panel">
        <div className="flex items-start gap-2 mb-5 text-sm" style={{ color: "var(--ink)" }}>
          <AlertTriangle size={16} style={{ color: "#B45309", flexShrink: 0, marginTop: 2 }}/>
          <p>
            Restoring replaces every record in this environment's database with the contents of the
            archive. Anything created since that archive was taken is lost. A safety backup of the
            current state is taken automatically first, so a restore can itself be undone.
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          <div>
            <label className="field-label">Stored backup</label>
            <select
              className="w-full border px-3 py-2 text-sm outline-none"
              style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}
              value={restore.filename}
              onChange={(e) => { setRestore({ ...restore, filename: e.target.value }); setFile(null); }}
              data-testid="restore-select"
            >
              <option value="">— choose —</option>
              {data.backups.map((entry) => (
                <option key={entry.filename} value={entry.filename}>
                  {when(entry.created_at)} · {KIND_LABEL[entry.kind] || entry.kind}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="field-label">…or upload an archive</label>
            <input type="file" accept=".gz" className="w-full text-sm"
                   onChange={(e) => { setFile(e.target.files?.[0] || null); setRestore({ ...restore, filename: "" }); }}
                   data-testid="restore-file"/>
          </div>
          <div>
            <label className="field-label">Type RESTORE to confirm</label>
            <input className="w-full border px-3 py-2 text-sm outline-none"
                   style={{ borderColor: "var(--border-strong)", background: "var(--bg)" }}
                   value={restore.confirm}
                   onChange={(e) => setRestore({ ...restore, confirm: e.target.value })}
                   data-testid="restore-confirm"/>
          </div>
        </div>
        <button onClick={runRestore} className="btn-primary mt-5 flex items-center gap-2"
                disabled={!canRestore || busy === "restore"} data-testid="run-restore">
          <RotateCcw size={14}/> {busy === "restore" ? "Restoring…" : "Restore this backup"}
        </button>
        {result && (
          <div className="mt-6 border p-4 text-sm" style={{ borderColor: "var(--border)", background: "var(--bg)" }} data-testid="restore-result">
            Restored from <span className="font-mono text-xs">{result.restored_from}</span> —{" "}
            {result.collections} collections, {result.users} users, {result.invoices} invoices. The
            pre-restore state is held in{" "}
            <span className="font-mono text-xs">{result.safety_backup}</span>.
          </div>
        )}
      </div>
    </div>
  );
}
